import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { H2, H3, P, UL, Note, TBD, LEGAL_ENTITY, PRODUCT, CONTACT_EMAIL } from './LegalLayout';

// NOTE FOR FUTURE EDITORS AND FOR COUNSEL
// --------------------------------------------------------------------------
// This page is different in kind from Terms / Privacy / HipaaNotice: those are
// policies we publish, this is an INSTRUMENT the practice executes. It is the
// BAA that Terms of Service §7 requires and that HipaaNotice promises to
// provide on request.
//
// Two consequences:
//
//   1. It must satisfy 45 C.F.R. § 164.504(e) and § 164.314(a) on its own.
//      Every element those sections require is present below and labelled, so
//      a reviewer can check them off. Do not delete a clause because it reads
//      as boilerplate — several are mandatory contract terms.
//
//   2. Publishing the text is not executing it. HOW it gets signed (a
//      click-through at signup, or a counter-signed PDF) is an open question
//      for counsel, flagged in §13. Until that is decided and UNDER_REVIEW is
//      flipped in LegalLayout.js, no practice has a BAA with us and no real
//      PHI may enter the system.
//
// Accuracy rule inherited from the other legal pages: every factual claim
// here describes what the code actually does today — the de-identification
// layer, link-only email/SMS, the BAA-gated fax channel, immutable signed
// submissions, the audit log, the subprocessor list. If one of those changes,
// this document changes in the same commit (CLAUDE.md inviolable rule 6).
// --------------------------------------------------------------------------

/** A numbered clause. `cite` prints the CFR provision the clause satisfies. */
function Cite({ children }) {
  return <span className="text-xs font-normal text-muted-foreground">{children}</span>;
}

export default function Baa() {
  return (
    <LegalLayout
      title="Business Associate Agreement"
      subtitle={`The HIPAA agreement between a medical practice and ${LEGAL_ENTITY} covering ${PRODUCT}.`}
      updated="October 22, 2026"
    >
      <Note>
        <strong>This is the agreement, not a description of one.</strong> Reading this page does not put a BAA in
        place — it is executed as described in Section 13. Your practice must have an executed BAA with us{' '}
        <strong>before any real patient information enters {PRODUCT}</strong>; evaluation accounts are loaded with
        synthetic data for that reason. The commercial terms sit in the{' '}
        <Link to="/terms" className="font-medium text-primary hover:underline">Terms of Service</Link>; where they and
        this Agreement conflict as to protected health information, this Agreement wins.
      </Note>

      <H2 id="parties">1. Parties, purpose, and effective date</H2>
      <P>
        This Business Associate Agreement (the "<strong>Agreement</strong>") is entered into between{' '}
        {LEGAL_ENTITY}, a Texas limited liability company doing business as DoctorPaperwork ("<strong>Business Associate</strong>",
        "<strong>we</strong>", "<strong>us</strong>"), and the medical practice identified in the signature block
        below (the "<strong>Covered Entity</strong>", "<strong>Practice</strong>", "<strong>you</strong>"). It takes
        effect on the date of the last signature, or on the date you first accept it in the Service, whichever is
        earlier.
      </P>
      <P>
        You are a covered entity, or a business associate acting for one, under the Health Insurance Portability and
        Accountability Act of 1996 and its implementing regulations at 45 C.F.R. Parts 160 and 164, as amended by the
        HITECH Act and the 2013 Omnibus Rule (together, "<strong>HIPAA</strong>"). We provide {PRODUCT}, software that
        collects patient-reported information, assembles medical leave, disability, and accommodation paperwork,
        produces AI-assisted draft content for your staff to review, and delivers the finished package after one of
        your clinicians signs it (the "<strong>Service</strong>", as defined in the Terms of Service). Providing the
        Service requires us to create, receive, maintain, and transmit protected health information on your behalf.
        HIPAA therefore requires this Agreement. <Cite>45 C.F.R. §§ 164.308(b), 164.502(e), 164.504(e)</Cite>
      </P>
      <P>
        This Agreement covers all protected health information we handle for you in connection with the Service,
        whenever created or received, including information handled before the effective date if any.
      </P>

      <H2 id="definitions">2. Definitions</H2>
      <P>
        Capitalized terms not defined here have the meanings given in 45 C.F.R. Parts 160 and 164. A reference to a
        section of the regulations means that section as amended from time to time.
      </P>
      <UL>
        <li><strong>PHI</strong> — Protected Health Information, as defined at 45 C.F.R. § 160.103, limited to information we create, receive, maintain, or transmit for or on behalf of the Practice.</li>
        <li><strong>ePHI</strong> — PHI held or transmitted in electronic form. In practice, all PHI we handle is ePHI.</li>
        <li><strong>Breach</strong> — as defined at 45 C.F.R. § 164.402.</li>
        <li><strong>Unsecured PHI</strong> — PHI not rendered unusable, unreadable, or indecipherable to unauthorized persons through a technology or methodology specified by HHS guidance under HITECH § 13402(h)(2).</li>
        <li><strong>Security Incident</strong> — as defined at 45 C.F.R. § 164.304.</li>
        <li><strong>Designated Record Set</strong> — as defined at 45 C.F.R. § 164.501.</li>
        <li><strong>Required By Law</strong> — as defined at 45 C.F.R. § 164.103.</li>
        <li><strong>Subcontractor</strong> — as defined at 45 C.F.R. § 160.103: a person or entity to whom we delegate a function, activity, or service involving the use or disclosure of PHI. The Service's subcontractors are listed in the <Link to="/privacy#share" className="font-medium text-primary hover:underline">Privacy Policy</Link>.</li>
        <li><strong>Document</strong> — a completed leave, disability, or accommodation form package prepared through the Service.</li>
        <li><strong>Terms</strong> — the {PRODUCT} <Link to="/terms" className="font-medium text-primary hover:underline">Terms of Service</Link>, or a signed master services agreement or order form that replaces them.</li>
      </UL>

      <H2 id="precedence">3. Relationship to the Terms of Service</H2>
      <P>
        This Agreement is incorporated into the Terms. As to PHI, this Agreement controls over any conflicting
        provision of the Terms, including the disclaimers in Terms §15 and the limitation of liability in Terms §16.
        As to everything else — fees, seats, service levels, intellectual property — the Terms control. Neither
        document limits our obligations under HIPAA itself.
      </P>

      <H2 id="permitted">4. How we may use and disclose PHI</H2>
      <P>
        We may use and disclose PHI only as this Agreement permits, as the Practice instructs in writing or through the
        Service, or as Required By Law. Every use below is also limited by Section 4.6.
        <Cite> 45 C.F.R. § 164.504(e)(2)(i)</Cite>
      </P>

      <H3>4.1 To provide the Service</H3>
      <P>
        We may use and disclose PHI as necessary to perform the Service for you: collecting patient intake answers,
        reading uploaded employer form pages, preparing draft form content, routing a Document for nurse review and
        physician signature, generating the signed PDF, and delivering it to the recipients you or the patient direct
        — an employer, insurer, or third-party administrator — by secure link, or by fax where you have enabled that
        channel. We may also use PHI to provide support you request, to investigate and fix problems, and to operate,
        secure, monitor, and back up the Service.
      </P>

      <H3>4.2 For our own management, administration, and legal obligations</H3>
      <P>
        We may use PHI for our proper management and administration and to carry out our legal responsibilities. We
        may disclose PHI for those purposes only if the disclosure is Required By Law, or if we obtain reasonable
        assurances from the recipient that the information will be held confidentially, used or further disclosed only
        as Required By Law or for the purpose for which it was disclosed, and that the recipient will notify us of any
        breach of its confidentiality. <Cite>45 C.F.R. § 164.504(e)(4)</Cite>
      </P>

      <H3>4.3 Data aggregation</H3>
      <P>
        We may use PHI to provide data aggregation services relating to your health care operations, as permitted by 45
        C.F.R. § 164.504(e)(2)(i)(B) — for example, combining your turnaround-time data with other practices' to give
        you a benchmark. Output of such aggregation released outside the Service will be de-identified under Section
        4.4.
      </P>

      <H3>4.4 De-identification — and what it means for our AI features</H3>
      <P>
        <strong>You authorize us to de-identify PHI</strong> in accordance with 45 C.F.R. § 164.514(a)–(b). This
        authorization is operationally central to the Service, not incidental, and it works in two places:
      </P>
      <UL>
        <li>
          <strong>Before every AI call.</strong> The Service uses large language models to draft narrative sections,
          summarize patient answers, read uploaded form pages, and answer staff questions. Patient information passes
          through a de-identification layer first, which removes Safe Harbor identifiers — patient, employer, and
          practice names, contact details, Social Security numbers, dates of birth, and all calendar dates, which are
          converted to durations or day offsets because an absolute date is itself an identifier. What the model
          receives is clinical context without identity. The complete record never leaves our database; only the
          outbound request is de-identified.
        </li>
        <li>
          <strong>For aggregated statistics about the Service.</strong> We create and use de-identified, aggregated
          usage data to operate and improve the Service, as described in Terms §9.
        </li>
      </UL>
      <Note>
        Information de-identified under § 164.514 is no longer PHI, and this Agreement does not restrict our use of
        it. Two commitments limit that in your favor, and both survive termination: we will not attempt to
        re-identify, or permit a Subcontractor to re-identify, information we de-identified from your PHI; and we will
        not use PHI or information derived from it to train, fine-tune, or improve any machine learning model, ours or
        anyone else's, nor permit a Subcontractor to do so. Our AI subcontractor is contractually barred from
        retaining prompts for model training.
      </Note>
      <P>
        De-identification reduces risk; it is not a substitute for the safeguards in Section 5. Our AI processing runs
        inside a Subcontractor relationship covered by a Business Associate Agreement regardless, so that a
        de-identification defect is contained rather than uncovered.
      </P>

      <H3>4.5 What we will not do</H3>
      <UL>
        <li>We will not use or disclose PHI in any manner that would violate Subpart E of 45 C.F.R. Part 164 if done by the Practice, except as § 164.504(e)(4) permits for our own management and administration and legal obligations. <Cite>§ 164.504(e)(2)(i)(A)</Cite></li>
        <li>We will not sell PHI, or receive direct or indirect remuneration in exchange for PHI, without a valid authorization. <Cite>§ 164.502(a)(5)(ii)</Cite></li>
        <li>We will not use or disclose PHI for marketing, fundraising, or advertising, and we do not share it for cross-context behavioral advertising.</li>
        <li>We will not use PHI to develop a competing product, or disclose it to any party for their own independent purposes.</li>
        <li>We will not disclose PHI to a patient's employer, insurer, or administrator except at the direction of the Practice or the patient through the Service.</li>
      </UL>

      <H3>4.6 Minimum necessary</H3>
      <P>
        We will request, use, and disclose only the minimum amount of PHI necessary to accomplish the purpose of the
        request, use, or disclosure, consistent with 45 C.F.R. §§ 164.502(b) and 164.514(d) and HHS guidance.
        <Cite> §§ 164.502(b), 164.514(d)</Cite>
      </P>

      <H2 id="safeguards">5. Safeguards</H2>
      <P>
        We will use appropriate administrative, physical, and technical safeguards, and — with respect to ePHI — comply
        with the HIPAA Security Rule at 45 C.F.R. §§ 164.308, 164.310, 164.312, and 164.316, to prevent use or
        disclosure of PHI other than as this Agreement permits. Those sections apply to us directly as a business
        associate, not only through this Agreement.
        <Cite> §§ 164.314(a)(2)(i)(A), 164.504(e)(2)(ii)(B)</Cite>
      </P>
      <P>
        The safeguards in place today are described in <Link to="/privacy#security" className="font-medium text-primary hover:underline">Privacy Policy §8</Link> and include: TLS on every connection; encryption at rest for the
        production database and file storage; server-enforced role-based access control with practice-level isolation
        on every query; unique accounts per person with hashed passwords, required authenticator-app multi-factor
        authentication, session expiry, lockout after repeated failed sign-ins, and session revocation on password
        change; audit logging of access to and disclosure of PHI; rate limiting on public and authentication
        endpoints; and time-limited, single-purpose patient links. We will maintain safeguards at least as protective
        for the term of this Agreement, and will maintain written policies and documentation as § 164.316 requires,
        retained for six years.
      </P>
      <Note>
        <strong>What we do not claim.</strong> We hold no HIPAA "certification" — none exists — and we do not represent
        that we hold a SOC 2 report or HITRUST certification unless we have given you one in writing. We will make our
        current security documentation and the results of our most recent risk analysis available to you on request
        under Section 8.5. Two items are still being completed before production PHI:{' '}
        the production database and file storage run on a dedicated MongoDB Atlas cluster with storage encryption at rest (AES-256){' '}
        and <TBD>data retention and disposal policy, workforce training log, and named Privacy/Security Officer —
        HIPAA checklist §7</TBD>. We will not accept production PHI from you before those are done.
      </Note>

      <H2 id="subcontractors">6. Subcontractors</H2>
      <P>
        We may use Subcontractors to help provide the Service. In accordance with §§ 164.308(b)(2), 164.314(a)(2)(iii),
        and 164.502(e)(1)(ii), we will not give PHI to a Subcontractor unless it has first agreed in writing to
        restrictions and conditions at least as protective as those that apply to us under this Agreement, including
        breach reporting to us.
      </P>
      <P>
        Our current Subcontractors, what each one does, and its BAA status are published in the{' '}
        <Link to="/privacy#share" className="font-medium text-primary hover:underline">Privacy Policy</Link>, which we
        keep current as a representation to you. Three points on that list matter contractually:
      </P>
      <UL>
        <li>
          <strong>Email and text messaging are outside BAA scope by design, not by omission.</strong> Our email and SMS
          bodies never contain PHI — they state that a document is ready and provide a secure link. This is a design
          constraint of the Service, so those vendors receive no PHI and need no BAA. We will not put PHI into an email
          or text message body under this Agreement. If that ever changes, we will move the channel to a vendor under
          a BAA and notify you first.
        </li>
        <li>
          <strong>Payment processing receives no PHI.</strong> Our payment processor receives the transaction data it
          needs and no clinical information. Terms §5 requires you to keep PHI out of fields not designated for it,
          including payment descriptions, which is what keeps this true.
        </li>
        <li>
          <strong>Fax is the one channel that transmits the Document itself</strong> — the signed packet, including
          patient name, condition, and dates. It is therefore disabled at the system level and cannot be switched on
          until a BAA with the fax vendor is executed, independently of whether credentials are configured. Where you
          enable fax, you direct the disclosure and are responsible for the correctness of the destination number.
        </li>
      </UL>
      <P>
        We will give you at least 30 days notice before a new
        Subcontractor begins handling your PHI, by email to your account administrators and by updating the published
        list. If you reasonably object on data-protection grounds within that period, we will work with you in good
        faith on an alternative; if none is available, either party may terminate the affected portion of the Service
        under Section 11 without penalty and with a pro-rata refund of prepaid fees.
      </P>
      <P>
        We remain responsible to you for our Subcontractors' handling of PHI to the same extent as for our own.
      </P>

      <H2 id="reporting">7. Reporting breaches, impermissible uses, and security incidents</H2>

      <H3>7.1 Breach of Unsecured PHI</H3>
      <P>
        We will notify you of a Breach of Unsecured PHI without unreasonable delay and in no event later than{' '}
        <strong>five (5) business days</strong> after discovery, and will supplement that notice as we learn more,
        providing the complete information below no later than <strong>fifteen (15) calendar days</strong> after
        discovery. A Breach is treated as discovered on the first day it is known, or by exercising reasonable
        diligence would have been known, to any of our employees, officers, or agents other than the person who
        committed it. <Cite>45 C.F.R. § 164.410</Cite>
      </P>
      <P>
        HIPAA's outer limit is 60 calendar days from discovery (§ 164.410(b)). We commit to a fraction of it
        deliberately: your own deadline to notify affected patients under § 164.404 is 60 days, and if we consumed all
        of it you would have none. An initial notice within five business days is not a complete forensic account —
        it is the trigger that lets your compliance officer start work while we finish investigating.
      </P>
      <P>Our notice will include, to the extent then known and thereafter as we learn it:</P>
      <UL>
        <li>the identification of each individual whose Unsecured PHI has been, or is reasonably believed to have been, accessed, acquired, used, or disclosed;</li>
        <li>a description of what happened, the date of the Breach, and the date of discovery;</li>
        <li>the types of PHI involved;</li>
        <li>what we have done to investigate, mitigate harm, and prevent recurrence; and</li>
        <li>any other information you need to meet your own notification obligations under § 164.404. <Cite>§ 164.410(c)</Cite></li>
      </UL>

      <H3>7.2 Impermissible use or disclosure</H3>
      <P>
        We will report to you any use or disclosure of PHI not permitted by this Agreement of which we become aware,
        including one by a Subcontractor, whether or not it constitutes a Breach, without unreasonable delay.
        <Cite> § 164.504(e)(2)(ii)(C)</Cite>
      </P>

      <H3>7.3 Security Incidents</H3>
      <P>
        We will report Security Incidents affecting ePHI to you as required by § 164.314(a)(2)(i)(C), on the timeline
        in Section 7.1 where the incident resulted in unauthorized access, use, disclosure, modification, or
        destruction of ePHI, and otherwise promptly.
      </P>
      <Note>
        <strong>The standard carve-out for background noise, stated plainly.</strong> Attempted but unsuccessful
        Security Incidents that do not result in unauthorized access to, or use, disclosure, modification, or
        destruction of ePHI — port scans, denial-of-service attempts our rate limiting absorbs, failed sign-in
        attempts, malformed requests, pings, and similar automated traffic — occur constantly against any
        internet-facing service. Reporting each one individually would be noise, not notice. This paragraph
        constitutes our notice to you of such attempts. We log them, and will provide an aggregate summary on your
        written request, no more often than <strong>quarterly</strong>.
      </Note>

      <H3>7.4 Mitigation</H3>
      <P>
        We will mitigate, to the extent practicable, any harmful effect known to us of a use or disclosure of PHI in
        violation of this Agreement. <Cite>§ 164.530(f), applied by contract</Cite>
      </P>

      <H3>7.5 Who notifies patients, HHS, and the media</H3>
      <P>
        Those duties are yours as the covered entity, under §§ 164.404, 164.406, and 164.408. We will not notify your
        patients, HHS, or the media on your behalf unless you instruct us in writing to do so and we agree in writing.
        We will support you: we will give you the facts in Section 7.1, help you assess whether the Breach-risk
        assessment under § 164.402(2) is satisfied, and — where we caused the Breach — cooperate on the content and
        logistics of notification.
      </P>
      <H3>7.6 Who pays for breach response</H3>
      <P>
        Where a Breach of Unsecured PHI results from our act or omission, or that of a Subcontractor, we will bear the
        reasonable and documented costs of responding to it: forensic investigation, the notification letters and
        substitute notice § 164.404 requires, a call line for affected patients where volume warrants one, and{' '}
        <TBD>whether to include credit monitoring, and for how long — 12 or 24 months is typical, and it is the
        single largest line item</TBD>. Where the Breach results from your act or omission, those costs are yours.
        Where both parties contributed, each bears the costs attributable to its own conduct.
      </P>
      <P>
        Our liability for these costs is capped at the greater of $[X] or 12 months' fees. Must be set no higher than the cyber-liability limit
        actually carried, and Terms §16's carve-out list must be amended to match, or the two documents will
        contradict each other. Nothing in this Section shifts to us any civil monetary penalty assessed against
        you for your own violation, or vice versa.
      </P>
      <P>
        Each party will maintain insurance appropriate to these obligations. We will maintain cyber-liability and
        technology errors-and-omissions coverage of at least <TBD>limit — obtain a broker quote before committing to a
        number; do not state a limit we do not carry</TBD>, and will provide a certificate of insurance on your
        written request.
      </P>
      <Note>
        <strong>Why this clause exists, for whoever negotiates it next.</strong> HIPAA does not require an allocation
        of breach costs — it is purely commercial. It is in here because the practice's compliance officer will ask,
        and a vendor that stays silent reads as a vendor that intends to leave the bill with them. The three blanks
        above are one decision with a price attached: the insurance limit we can fund sets the cap, and the cap sets
        what we can honestly promise. Get the quote first, then fill all three in the same edit — and amend Terms §16
        in the same commit.
      </Note>

      <H2 id="individual-rights">8. Supporting patients' individual rights</H2>
      <P>
        Patients exercise HIPAA rights against you, not against us — we hold their information on your behalf and say
        so plainly on our patient-facing{' '}
        <Link to="/hipaa" className="font-medium text-primary hover:underline">HIPAA page</Link>. If a patient
        contacts us directly, we will refer them to you and tell you they asked. To let you respond, we will do the
        following.
      </P>

      <H3>8.1 Access</H3>
      <P>
        Where we hold PHI in a Designated Record Set, we will make it available to you — or, at your written
        direction, to the individual or their designee — so you can meet § 164.524, within{' '}
        10 business days of your request. In most cases you will not need to
        ask: administrators can view and export a patient's intake answers, Documents, and audit trail directly in the
        Service at any time. <Cite>§ 164.504(e)(2)(ii)(E)</Cite>
      </P>

      <H3>8.2 Amendment</H3>
      <P>
        We will make PHI available to you for amendment, and incorporate an amendment you direct, so you can meet
        § 164.526, within 10 business days.
        <Cite> § 164.504(e)(2)(ii)(F)</Cite>
      </P>
      <Note>
        <strong>One design fact you should know before you rely on this.</strong> A signed Document is immutable in the
        Service — we do not overwrite a record a clinician has signed, because silently altering a signed medical
        document would destroy its evidentiary value. Amendments are made the way the medical record itself handles
        them: through the revision-request workflow, which records the requested correction, the clinician's
        acknowledgement, and a newly signed superseding Document, with the original and the amendment both retained
        and linked. That satisfies § 164.526, which requires that an amendment be appended and linked, not that the
        original be erased. If you need a signed Document destroyed rather than amended, that is a Section 11.3
        request and requires your written instruction.
      </Note>

      <H3>8.3 Accounting of disclosures</H3>
      <P>
        We will document disclosures of PHI and information related to them as would be required for you to respond to
        a request for an accounting under § 164.528, and will provide that documentation to you within{' '}
        15 business days of your request.
        <Cite> §§ 164.504(e)(2)(ii)(G), 164.528</Cite>
      </P>
      <P>
        The Service logs every access to, preparation of, signature on, and delivery of a Document — the acting user,
        the action, the target, and the timestamp — and administrators can review it. Two honest notes on scope:
        actions taken by a patient through their own secure link are logged without patient identifiers in the actor
        field, by design, because the audit log itself must not become a second copy of PHI; those entries are
        nonetheless linked to the submission, so a per-patient accounting can be produced. And delivery records
        capture what the Service transmitted and to which destination, which is the disclosure set § 164.528 is
        concerned with.
      </P>

      <H3>8.4 Restrictions and confidential communications</H3>
      <P>
        If you agree to a restriction on use or disclosure under § 164.522, or to a confidential-communications
        request, tell us in writing and we will comply to the extent it affects our handling of that patient's PHI. You
        will also tell us about any change to, or revocation of, a patient's permission or authorization, or any
        change to your Notice of Privacy Practices, that affects our permitted uses or disclosures.
        <Cite> § 164.522</Cite>
      </P>
      <P>
        Because delivery works by link to the contact details on file, a confidential-communications request will
        usually be satisfied by updating those details in the Service — which you can do without us.
      </P>

      <H3>8.5 Availability of our records</H3>
      <P>
        We will make our internal practices, books, and records relating to the use and disclosure of your PHI
        available to the Secretary of Health and Human Services for purposes of determining your compliance with
        HIPAA. We will notify you of such a request unless prohibited by law.
        <Cite> § 164.504(e)(2)(ii)(I)</Cite>
      </P>
      <P>
        On your reasonable written request, no more than once per year absent a
        Breach or a regulatory inquiry, we will provide our current security documentation, our most recent risk
        analysis summary, our Subcontractor list, and written responses to a security questionnaire. Any on-site or
        technical audit right beyond that — and penetration testing, which Terms §5 otherwise prohibits — requires our
        prior written agreement on scope and timing. <TBD>Counsel: whether to grant a broader audit right; large
        health systems will ask for one.</TBD>
      </P>

      <H3>8.6 Carrying out your obligations</H3>
      <P>
        To the extent we carry out one of your obligations under Subpart E of 45 C.F.R. Part 164, we will comply with
        the requirements of Subpart E that apply to you in the performance of that obligation.
        <Cite> § 164.504(e)(2)(ii)(H)</Cite>
      </P>

      <H2 id="ce-obligations">9. Your obligations as Covered Entity</H2>
      <P>
        This Agreement depends on you doing several things we cannot do for you. You agree that:
      </P>
      <UL>
        <li>you will obtain and maintain whatever patient consent, authorization, or notice applicable law requires before submitting PHI to the Service and before directing a Document to an employer, insurer, or administrator;</li>
        <li>your Notice of Privacy Practices, and any changes to it, permit the uses and disclosures this Agreement contemplates, including disclosure to a technology vendor and to that vendor's subcontractors;</li>
        <li>you will not request us to use or disclose PHI in a way that would not be permitted under Subpart E of Part 164 if done by you, except as § 164.504(e)(4) permits;</li>
        <li>each clinician who signs a Document holds a current, unrestricted license in the relevant jurisdiction and has a treatment relationship with the patient, as Terms §5 also requires;</li>
        <li>you will keep PHI out of fields not designated for it — payment descriptions, practice display names, support tickets, and product feedback;</li>
        <li>you will keep patient contact details current, since Documents are delivered by link to the address or number on file, and you will verify a fax destination before enabling fax delivery;</li>
        <li>you will manage your own users: unique accounts per person, no shared credentials, prompt deactivation of anyone who leaves or changes role, and periodic access review. We enforce authentication; only you know who should still have access;</li>
        <li>you will report to us, without unreasonable delay, any suspected compromise of your account credentials or any use of the Service you believe violates this Agreement.</li>
      </UL>

      <H2 id="state-law">10. State law</H2>
      <P>
        Where state law affords patients greater protection than HIPAA, or imposes additional duties on either party,
        both parties will comply with it, and this Agreement is to be read as requiring the more protective standard.
      </P>
      <Note>
        <TBD>Counsel — this is a live issue for our first customers, not a hypothetical. The Texas Medical Records
        Privacy Act (Tex. Health &amp; Safety Code ch. 181) defines "covered entity" far more broadly than HIPAA and
        reaches vendors that assemble, collect, or transmit PHI — which would make {LEGAL_ENTITY} a covered entity
        under Texas law in its own right, with independent obligations including workforce training within 90 days of
        hire (§ 181.101) and state-law penalties. Confirm the analysis, decide whether this Agreement should state it
        expressly, and identify the equivalent statutes in every state where we intend to operate. Also confirm
        whether any pilot practice's state imposes additional consent requirements for disclosing leave paperwork to
        an employer.</TBD>
      </Note>

      <H2 id="term">11. Term, termination, and what happens to PHI</H2>

      <H3>11.1 Term</H3>
      <P>
        This Agreement begins on the effective date and continues until all PHI we hold for you has been returned,
        destroyed, or protected as Section 11.3 provides. It survives termination of the Terms.
      </P>

      <H3>11.2 Termination for cause</H3>
      <P>
        You may terminate this Agreement and the Terms if we materially breach this Agreement and fail to cure within{' '}
        30 days of your written notice, or immediately if cure is
        not possible. We have the same right if you materially breach Section 9. Where neither cure nor termination is
        feasible, either party may report the problem to HHS.
        <Cite> § 164.504(e)(1)(ii)</Cite>
      </P>
      <P>
        We may suspend the Service, as Terms §10 provides, if PHI is submitted without an executed BAA or your account
        is used in a way that threatens the confidentiality of PHI. Suspension does not end this Agreement.
      </P>

      <H3>11.3 Return or destruction of PHI</H3>
      <P>
        On termination, you may export your data through the Service. On your written request made within{' '}
        30 days, we will make your PHI available
        for export in a usable electronic format. After that window, and at your written direction, we will return or
        destroy all PHI we or our Subcontractors hold, and retain no copies, to the extent feasible.
        <Cite> § 164.504(e)(2)(ii)(J)</Cite>
      </P>
      <P>
        <strong>Where return or destruction is not feasible</strong> — because PHI persists in encrypted backups until
        they expire on their normal rotation, in immutable audit records HIPAA itself requires us to retain for six
        years under § 164.316(b)(2), or because a legal hold, subpoena, or retention obligation applies — we will
        notify you of the specific reason, extend every protection of this Agreement to that information for as long
        as we hold it, and limit further use or disclosure to the purpose that makes destruction infeasible. Backup
        copies are destroyed as their retention cycle completes, within{' '}
        <TBD>backup retention period, e.g. 35 days</TBD> of the destruction instruction.
      </P>
      <P>
        Destruction will use a method HHS guidance recognizes as rendering PHI unusable, unreadable, and
        indecipherable, including cryptographic erasure where the data is encrypted at rest. We will certify
        destruction in writing on request.
      </P>

      <H2 id="general">12. General</H2>
      <UL>
        <li><strong>Amendment to stay compliant.</strong> The parties will amend this Agreement as necessary for it to remain compliant with HIPAA and other applicable law. We may propose an amendment on 30 days written notice; if you object to a proposed amendment that is not required by law, the prior version stays in effect for the remainder of your current term.</li>
        <li><strong>Interpretation.</strong> Any ambiguity is resolved in favor of a meaning that permits both parties to comply with HIPAA. No provision is to be read as authorizing a use or disclosure HIPAA prohibits.</li>
        <li><strong>Regulatory references.</strong> A citation to a regulation includes that regulation as amended and any successor provision.</li>
        <li><strong>No third-party beneficiaries.</strong> This Agreement creates no rights in any patient or other third party, and nothing here is intended to create a private right of action. It does not diminish any right a patient has against you under HIPAA or state law.</li>
        <li><strong>Independent parties.</strong> We are an independent contractor, not your agent. Nothing here creates a partnership, joint venture, or employment relationship, and neither party may bind the other. <TBD>Counsel to confirm the agency disclaimer is consistent with the conduit/agent analysis under § 164.402(2) and HHS commentary — a business associate found to be an agent exposes the covered entity to imputed liability for breaches.</TBD></li>
        <li><strong>Survival.</strong> Sections 4.4 (the re-identification and model-training commitments), 5, 7, 8.5, 11.3, and this Section 12 survive termination.</li>
        <li><strong>Governing law and disputes.</strong> As stated in Terms §19, except that nothing there limits either party's obligations under HIPAA. the State of Texas, with venue in the state and federal courts located in Bexar County, Texas (as in Terms §19)</li>
        <li><strong>Assignment.</strong> Neither party may assign this Agreement without the other's written consent, except to a successor in a merger or sale of substantially all assets, with notice; a successor is bound by this Agreement.</li>
        <li><strong>Counterparts and electronic signature.</strong> This Agreement may be executed in counterparts and by electronic signature, each of which is an original, and is valid under the federal E-SIGN Act and applicable state UETA law.</li>
        <li><strong>Entire agreement on this subject.</strong> This Agreement, with the Terms, is the parties' entire agreement on the handling of PHI and supersedes any prior business associate agreement between them, which is terminated on the effective date except as to PHI whose disposition it governs.</li>
        <li><strong>Notices.</strong> Notices to us go to 530 Berwick Town, San Antonio, TX 78249 and <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>; notices to you go to the address and account-administrator email addresses on file. Breach notices under Section 7 will be sent by email <strong>and</strong> by <TBD>second channel for breach notice, e.g. telephone to a named security contact</TBD>, since email alone may fail exactly when it matters.</li>
        <li><strong>Insurance.</strong> As stated in Section 7.6.</li>
      </UL>

      <H2 id="execution">13. How this Agreement is executed</H2>
      <P>
        This Agreement may be executed either way, and both routes produce the same binding agreement:
      </P>
      <UL>
        <li>
          <strong>Electronically, in the Service.</strong> An authorized representative of the Practice accepts this
          Agreement by name when the practice account is created. We record which version was accepted, the accepting
          person's name, role, and account, the date and time, and the network address the acceptance came from, and we
          make that record available to you. Acceptance is a signature under the federal E-SIGN Act and applicable
          state UETA law, as Terms §12 provides.
        </li>
        <li>
          <strong>On paper, countersigned.</strong> On request we will provide this Agreement as a document for
          signature, execute it, and return a countersigned copy for your compliance file. Practices whose policies
          require a wet or negotiated signature — and most health systems' do — should take this route. Ask at{' '}
          <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>.
        </li>
      </UL>
      <P>
        Either way you may download an executed copy showing the version in force for your practice and how it was
        executed. If you accept electronically and later request paper, the paper copy supersedes and carries the
        original effective date.
      </P>
      <P>
        A practice that has not executed this Agreement by one of these routes does not have a BAA with us, and no real
        patient information may be submitted to the Service. Terms §7 and §10 make submitting PHI without an executed
        BAA a breach and a ground for suspension for exactly this reason.
      </P>
      <Note>
        <strong>Implementation note — this paragraph describes behavior that must exist before it is true.</strong>{' '}
        Electronic acceptance requires the Service to record, per practice: the accepted version identifier, the
        accepting user, the timestamp, the network address, and the route (electronic or paper); to expose that record
        and a downloadable executed copy to practice administrators; and to write an{' '}
        <code className="rounded bg-muted px-1 text-xs">audit.log_action</code> entry on acceptance. Until that ships
        and counsel has approved this text, <code className="rounded bg-muted px-1 text-xs">UNDER_REVIEW</code> stays{' '}
        <code className="rounded bg-muted px-1 text-xs">true</code> in{' '}
        <code className="rounded bg-muted px-1 text-xs">LegalLayout.js</code> and no acceptance may be captured —
        collecting assent to a document we publicly label "not in effect" would be worse than collecting none.
      </Note>
      <P>
        <strong>Practice</strong>
      </P>
      <UL>
        <li>Legal name of practice: <TBD>to be completed on execution</TBD></li>
        <li>Signatory name and title: <TBD>to be completed on execution</TBD></li>
        <li>Notice address: <TBD>to be completed on execution</TBD></li>
        <li>HIPAA Privacy/Security contact and phone: <TBD>to be completed on execution</TBD></li>
        <li>Signature and date: <TBD>to be completed on execution</TBD></li>
      </UL>
      <P>
        <strong>{LEGAL_ENTITY}</strong>
      </P>
      <UL>
        <li>Signatory name and title: Colin Harris, Member</li>
        <li>Notice address: 530 Berwick Town, San Antonio, TX 78249</li>
        <li>HIPAA contact: Colin Harris, Privacy and Security Officer</li>
        <li>Signature and date: <TBD>to be completed on execution</TBD></li>
      </UL>

      <H2 id="contact">14. Questions</H2>
      <P>
        For a countersigned copy of this Agreement, our security documentation, or our subprocessor list:{' '}
        <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>. If your compliance team needs changes to
        this text, send them the same way — we would rather negotiate a clause than have you sign something you cannot
        defend in an audit.
      </P>
    </LegalLayout>
  );
}
